import { useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './viewLock.css';
import './stateDetail.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AiCombo } from '../../../components/AiCombo';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { api, runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { StateCitiesPanel } from './StateCitiesPanel';
import type { StagedCity } from './city';
import { useStates, useStateCountries, useSaveState, useDeleteState, errMessage, type StateRow } from './systemMasters';

interface Props { onHome?: () => void; }

/** Settings ▸ System Management ▸ State — CRUD over dbo.CustomState (FK → Country).
 *  List: New · Edit · Delete · Refresh · Export to · Search. The row icon and the toolbar Edit (one ticked row) open
 *  the full-page detail with the City grid ready to edit (the in-row editor was removed, owner request 2026-10-15);
 *  New opens it blank, a click on a row opens it read-only with Edit. */
export function StatePage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useStates();
  const { data: countries = [] } = useStateCountries();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveState();
  const del = useDeleteState();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editing, setEditing] = useState<StateRow | null | undefined>(undefined);
  const [longName, setLongName] = useState('');
  const [shortName, setShortName] = useState('');
  const [countryId, setCountryId] = useState('');
  const [err, setErr] = useState('');
  // Cities staged in memory on a brand-new (unsaved) state — committed when the state is saved.
  const [stagedCities, setStagedCities] = useState<StagedCity[]>([]);
  const qc = useQueryClient();

  // View → Edit (owner request 2026-10-12): a click on a row opens the record READ-ONLY with an Edit
  // button; Edit turns the boxes and Save on. New always opens editable.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewOnly = mode === 'view' && !!editing?.id;
  const openNew = () => { setMode('edit'); setEditing(null); setLongName(''); setShortName(''); setCountryId(''); setErr(''); setStagedCities([]); };
  const openView = (r: StateRow) => { openEdit(r); setMode('view'); };
  const openEdit = (r: StateRow) => { setMode('edit'); setEditing(r); setLongName(r.longName ?? ''); setShortName(r.shortName ?? ''); setCountryId(r.countryId ?? ''); setErr(''); setStagedCities([]); };
  const close = () => { setEditing(undefined); setStagedCities([]); };
  // Cancel / breadcrumb / record arrows: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  // Toolbar Edit: the ONE ticked row opens in the Detail View, editable (owner request 2026-10-15 — the in-row
  // editor was removed; the row icon opens the same Detail View).
  const editSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = [...selectedIds];
    const r = rows.find((x) => x.id === id);
    if (r) openEdit(r);
  };

  const submit = async (after: 'stay' | 'close' | 'new' = 'close') => {
    if (viewOnly) return;   // nothing to save while only viewing
    if (!longName.trim()) { setErr('State must not be empty.'); return; }
    setErr('');
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (editing?.id) {
      const r = await gateReason('State', 'edit');
      if (r === null) return;
      reason = r;
    }
    const wasNew = !editing?.id;
    let savedId: string | undefined;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const res = await save.mutateAsync({ id: editing?.id, longName: longName.trim(), shortName: shortName.trim() || null, countryId: countryId || null });
        savedId = editing?.id ?? res ?? undefined;
        // New state: commit the cities staged on the City grid (link an existing one or create it),
        // then each city's staged areas — the same chain the Country detail commits.
        if (wasNew && savedId && stagedCities.length) {
          for (const sc of stagedCities) {
            let cityId = sc.cityId;
            if (cityId) await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId: savedId });
            else cityId = ((await api.post('/api/v1/cities', { cityName: sc.name, stateId: savedId })) as { data?: { data?: { id?: string } } })?.data?.data?.id;
            if (cityId && sc.areas?.length) {
              for (const a of sc.areas) {
                if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
              }
            }
          }
          await qc.invalidateQueries({ queryKey: ['sm-cities'] });
          await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        // 'stay' — a brand-new record becomes the one being edited; if its id did not come back, return to the list.
        const country = countries.find((c) => c.id === countryId)?.name ?? null;
        setStagedCities([]);
        if (wasNew) { if (savedId) setEditing({ id: savedId, longName: longName.trim(), shortName: shortName.trim() || null, countryId: countryId || null, country } as StateRow); else close(); }
        else if (editing) setEditing({ ...editing, longName: longName.trim(), shortName: shortName.trim() || null, countryId: countryId || null, country });
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one state to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} state${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('State', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(editing === undefined ? [] : viewOnly ? [
    { combo: 'alt+e', label: 'Edit', group: 'State', order: 1, run: () => setMode('edit') },
    { combo: 'f2', label: 'Edit', group: 'State', order: 2, run: () => setMode('edit') },
    { combo: 'esc', label: 'Close', group: 'State', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'State', order: 1, run: () => void submit('stay') },
    { combo: 'mod+shift+s', label: 'Save & New', group: 'State', order: 2, run: () => void submit('new') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'State', order: 3, run: () => void submit('close') },
    { combo: 'esc', label: 'Cancel', group: 'State', order: 4, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.longName, r.shortName, r.country].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const cmp = (get: (r: StateRow) => string | null) => (a: StateRow, b: StateRow) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });

  const columns: AppColumn<StateRow>[] = [
    {
      // Opens the Detail View in edit mode (owner request 2026-10-15; was the in-row editor).
      field: 'detailedit', header: '', headerText: 'Edit', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (r) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.longName ?? 'state'}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-edit" /></button>
      ),
    },
    {
      field: 'longName', header: 'State', minWidth: 200, text: (r) => r.longName ?? '', compare: cmp((r) => r.longName),
      render: (r) => <span>{r.longName ?? ''}</span>,
    },
    {
      field: 'shortName', header: 'ShortName', width: 140, sortable: false, text: (r) => r.shortName ?? '',
      render: (r) => <span>{r.shortName ?? ''}</span>,
    },
    {
      field: 'country', header: 'Country', minWidth: 180, sortable: false, text: (r) => r.country ?? '',
      render: (r) => <span>{r.country ?? ''}</span>,
    },
  ];

  // ── Full-page detail (New / row click) — replaces the list while open (standing rule 12; owner
  //    request 2026-10-12: legacy opens the detail view, not a popup). A row opens READ-ONLY with
  //    an Edit button; New opens editable. State · Short Name · Country, then the City grid. ──
  if (editing !== undefined) {
    const recordName = editing ? (editing.longName ?? '').trim() || 'State' : 'New State';
    const curIdx = editing ? rows.findIndex((r) => r.id === editing.id) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const go = (r: StateRow) => void guardedExit(() => (viewOnly ? openView : openEdit)(r));
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void submit('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}</button>
        <button className="sd-abtn" onClick={() => void submit('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        <button className="sd-abtn" onClick={() => void submit('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>State – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>State</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && go(prevRow)}>‹</button>
                <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && go(nextRow)}>›</button>
              </div>
            </div>

            {actions()}

            <div className="sd-body sta-detail">
              <fieldset className="sm-viewlock sta-fields" disabled={viewOnly}>
                <div className="acc-field-h">
                  <label className="acc-label acc-label-req">State<span className="req">*</span></label>
                  <div className="acc-fh-control">
                    <input className="acc-input" autoFocus={!viewOnly} value={longName} onChange={(e) => { setLongName(e.target.value); if (err) setErr(''); }} />
                    {err && <div className="acc-err">{err}</div>}
                  </div>
                </div>
                <div className="acc-field-h">
                  <label className="acc-label">Short Name</label>
                  <div className="acc-fh-control">
                    <input className="acc-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
                  </div>
                </div>
                <div className="acc-field-h">
                  <label className="acc-label">Country</label>
                  <div className="acc-fh-control">
                    <AiCombo value={countryId} options={countries.map((c) => ({ value: c.id, label: c.name }))}
                             onChange={(v) => setCountryId(v ?? '')} />
                  </div>
                </div>
              </fieldset>
              {/* City child grid — State → City, as legacy CustomState_DetailView. A saved state shows its
                  live cities; a new one stages them until the state is saved. */}
              <div className={viewOnly ? 'sm-viewlock-panel' : undefined}>
                <StateCitiesPanel stateId={editing?.id} staged={stagedCities} onStaged={setStagedCities} inlineEdit={false} />
              </div>
            </div>

            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>State</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) openView(r);
            }} />
          <FormTitle pageId="sm-state" loadMs={loadMs ?? null} loading={isFetching} auditFormName="State" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action bar as legacy: New · Delete · Refresh · Export to · Search (no Edit — a row opens by a
            click, or is edited in place with its pencil). */}
        {!gridMax && (
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="State" headers={['State', 'ShortName', 'Country']} rows={visible.map((r) => [r.longName ?? '', r.shortName ?? '', r.country ?? ''])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="State" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<StateRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openView(r)}
            initialSort={{ key: 'longName', dir: 'asc' }} loading={isFetching} emptyText="No states found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
